// TODO: Agregar tipos correctos
// ===========================================
// BRAINHUB CORE: Provider de Workspace
// ===========================================

import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useOrganizacion } from './OrganizacionProvider'
import type {
  Workspace,
  WorkspaceId,
  WorkspaceContextType,
  NavigationItem,
  WorkspaceModule,
} from '../types/workspace'

// Importar definiciones de workspaces
import { WORKSPACE_CONTABLE } from '@/workspaces/contable'

// ===========================================
// REGISTRO DE WORKSPACES
// ===========================================

const ALL_WORKSPACES: Workspace[] = [
  WORKSPACE_CONTABLE,
  // WORKSPACE_JURIDICO se agregara cuando este listo
]

// ===========================================
// CONTEXTO
// ===========================================

const WorkspaceContext = createContext<WorkspaceContextType | null>(null)

// ===========================================
// PROVIDER
// ===========================================

interface WorkspaceProviderProps {
  children: ReactNode
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { workspacesHabilitados, workspacesUsuario, isLoading: orgLoading } = useOrganizacion()

  // Workspace guardado en localStorage
  const [workspaceGuardado, setWorkspaceGuardado] = useLocalStorage<WorkspaceId>(
    'brainhub-workspace-activo',
    'contable'
  )

  // Workspaces disponibles (interseccion: habilitados en org Y accesibles por usuario)
  const workspacesDisponibles = useMemo(() => {
    return ALL_WORKSPACES.filter(
      ws =>
        workspacesHabilitados.includes(ws.id) &&
        workspacesUsuario.includes(ws.id)
    )
  }, [workspacesHabilitados, workspacesUsuario])

  // Workspace actual
  const workspaceActual = useMemo(() => {
    // Buscar el guardado
    const guardado = workspacesDisponibles.find(ws => ws.id === workspaceGuardado)
    // Si existe, usarlo; si no, usar el primero disponible
    return guardado || workspacesDisponibles[0] || WORKSPACE_CONTABLE
  }, [workspacesDisponibles, workspaceGuardado])

  // Cambiar workspace
  const cambiarWorkspace = useCallback((id: WorkspaceId) => {
    if (workspacesDisponibles.some(ws => ws.id === id)) {
      setWorkspaceGuardado(id)
    }
  }, [workspacesDisponibles, setWorkspaceGuardado])

  // Verificar acceso a workspace
  const tieneAccesoWorkspace = useCallback((id: WorkspaceId) => {
    return workspacesDisponibles.some(ws => ws.id === id)
  }, [workspacesDisponibles])

  // Obtener navegacion del workspace actual
  const getNavigation = useCallback((): NavigationItem[] => {
    if (!workspaceActual) return []

    const items: NavigationItem[] = []

    // Crear mapa de modulo.id -> grupo.id
    const moduloToGroup = new Map<string, string>()
    workspaceActual.navigationGroups.forEach(group => {
      group.items.forEach(moduloId => {
        moduloToGroup.set(moduloId, group.id)
      })
    })

    // Recorrer modulos y extraer navegacion.
    // El group del item (si lo declara) gana sobre el grupo del módulo (ORR-192):
    // permite separar items que viven en el mismo módulo (ej. Presupuestos → 'ventas').
    workspaceActual.modulos.forEach(modulo => {
      const moduloGroup = moduloToGroup.get(modulo.id) || modulo.id
      modulo.navigation.forEach(navItem => {
        items.push({
          ...navItem,
          group: navItem.group ?? moduloGroup,
        })
      })
    })

    return items
  }, [workspaceActual])

  // Obtener modulos del workspace actual
  const getModulos = useCallback((): WorkspaceModule[] => {
    return workspaceActual?.modulos || []
  }, [workspaceActual])

  // Verificar si un modulo esta habilitado
  const tieneModulo = useCallback((moduloId: string): boolean => {
    return workspaceActual?.modulos.some(m => m.id === moduloId) || false
  }, [workspaceActual])

  // Valor del contexto
  const value = useMemo<WorkspaceContextType>(() => ({
    workspaceActual,
    workspacesDisponibles,
    cambiarWorkspace,
    tieneAccesoWorkspace,
    getNavigation,
    getModulos,
    tieneModulo,
    isLoading: orgLoading,
  }), [
    workspaceActual,
    workspacesDisponibles,
    cambiarWorkspace,
    tieneAccesoWorkspace,
    getNavigation,
    getModulos,
    tieneModulo,
    orgLoading,
  ])

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  )
}

// ===========================================
// HOOK
// ===========================================

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace debe usarse dentro de WorkspaceProvider')
  }
  return context
}

// ===========================================
// HOOK AUXILIAR: useLocalStorage (respaldo)
// ===========================================
// Respaldo interno en caso de que el hook externo no este disponible

export function useLocalStorageInternal<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (err) {
      console.warn(`[useLocalStorage] no se pudo leer "${key}":`, err)
      return initialValue
    }
  })

  const setValue = (value: T) => {
    try {
      setStoredValue(value)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(value))
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  }

  return [storedValue, setValue]
}
